import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import appAPI from '../../api/api'
import { RootState } from '../store'
import { EventsCommentCreate } from '../../api/model/eventsCommentCreate'
import { EventsCommentUpdate } from '../../api/model/eventsCommentUpdate'
import { EventsCommentRead } from '../../api/model/eventsCommentRead'

export type CommentsOrdering = 'new' | 'old'

type EventCommentsState = {
  comments: EventsCommentRead[]
  loading: boolean
  loadingMore: boolean
  submitting: boolean
  error?: string
  ordering: CommentsOrdering
  totalTopLevelComments: number
  rootLimit: number
  rootOffset: number
  hasMore: boolean
}

const ROOT_COMMENTS_PAGE_SIZE = 10
const CHILDREN_COMMENTS_PAGE_SIZE = 100

const initialState: EventCommentsState = {
  comments: [],
  loading: false,
  loadingMore: false,
  submitting: false,
  ordering: 'new',
  totalTopLevelComments: 0,
  rootLimit: ROOT_COMMENTS_PAGE_SIZE,
  rootOffset: 0,
  hasMore: false,
}

type FetchEventCommentsPayload = {
  target_id: number
  target_type: 'event' | 'news'
  ordering?: CommentsOrdering
}

type FetchMoreEventCommentsPayload = {
  target_id: number
  target_type: 'event' | 'news'
  ordering?: CommentsOrdering
}

type CreateEventCommentPayload = Partial<EventsCommentCreate> & {
  target_id_for_refetch: number
  ordering?: CommentsOrdering
}

type UpdateEventCommentPayload = {
  id: number
  target_id: number
  target_type: 'event' | 'news'
  ordering?: CommentsOrdering
} & EventsCommentUpdate

type DeleteEventCommentPayload = {
  id: number
  target_id: number
  target_type: 'event' | 'news'
  ordering?: CommentsOrdering
}

type GetEventsCommentsPayload = {
  parent?: number | null
  target_id: number
  target_type: 'event' | 'news'
  ordering?: CommentsOrdering
  limit?: number
  offset?: number
}

type RootCommentsPageResult = {
  comments: EventsCommentRead[]
  total: number
  nextOffset: number
  hasMore: boolean
}

type CommentsWithPaginationResult = {
  comments: EventsCommentRead[]
  totalTopLevelComments: number
  nextOffset: number
  hasMore: boolean
  rootLimit: number
}

const getResponseTotal = (data: any, fallback: number): number => {
  if (typeof data.count === 'number') return data.count
  if (typeof data.total === 'number') return data.total

  return fallback
}

const loadRootCommentsPage = async (payload: GetEventsCommentsPayload): Promise<RootCommentsPageResult> => {
  const limit = payload.limit || ROOT_COMMENTS_PAGE_SIZE
  const offset = payload.offset || 0

  const response = await appAPI.eventsComments.getEventsComments({
    ...payload,
    parent: null,
    limit,
    offset,
  })

  const items = response.data.results || []
  const responsePages = response.data.pages || 1
  const currentPage = Math.floor(offset / limit) + 1
  const fallbackTotal = responsePages * limit
  const total = getResponseTotal(response.data, fallbackTotal)
  const nextOffset = offset + items.length
  const hasMore = nextOffset < total && currentPage < responsePages + 1

  return {
    comments: items,
    total,
    nextOffset,
    hasMore,
  }
}

const loadChildrenCommentsPage = async (payload: GetEventsCommentsPayload): Promise<EventsCommentRead[]> => {
  let offset = 0
  let pages = 1
  let currentPage = 1
  const result: EventsCommentRead[] = []

  while (currentPage <= pages) {
    const response = await appAPI.eventsComments.getEventsComments({
      ...payload,
      limit: CHILDREN_COMMENTS_PAGE_SIZE,
      offset,
    })

    const items = response.data.results || []
    const responsePages = response.data.pages || 1

    result.push(...items)

    pages = responsePages
    currentPage += 1
    offset = (currentPage - 1) * CHILDREN_COMMENTS_PAGE_SIZE
  }

  return result
}

const loadChildrenForRootsRecursively = async (
  rootComments: EventsCommentRead[],
  target_id: number,
  target_type: 'event' | 'news',
  ordering: CommentsOrdering,
): Promise<EventsCommentRead[]> => {
  const allChildren: EventsCommentRead[] = []
  const queue: EventsCommentRead[] = [...rootComments]

  while (queue.length > 0) {
    const currentComment = queue.shift()

    if (!currentComment || !currentComment.childrenCount) continue

    const children = await loadChildrenCommentsPage({
      target_id,
      target_type,
      parent: currentComment.id,
      ordering,
    })

    allChildren.push(...children)

    children.forEach((child) => {
      if (child.childrenCount > 0) {
        queue.push(child)
      }
    })
  }

  return allChildren
}

const loadCommentsByRootPage = async (
  target_id: number,
  target_type: 'event' | 'news',
  ordering: CommentsOrdering,
  limit: number,
  offset: number,
): Promise<CommentsWithPaginationResult> => {
  const rootPage = await loadRootCommentsPage({
    target_id,
    target_type,
    ordering,
    limit,
    offset,
  })

  const children = await loadChildrenForRootsRecursively(rootPage.comments, target_id, target_type, ordering)

  return {
    comments: [...rootPage.comments, ...children],
    totalTopLevelComments: rootPage.total,
    nextOffset: rootPage.nextOffset,
    hasMore: rootPage.hasMore,
    rootLimit: limit,
  }
}

const fetchEventComments = createAsyncThunk<
  CommentsWithPaginationResult,
  FetchEventCommentsPayload,
  { state: RootState }
>('eventComments/fetchEventComments', async (payload) => {
  return loadCommentsByRootPage(
    payload.target_id,
    payload.target_type,
    payload.ordering || 'new',
    ROOT_COMMENTS_PAGE_SIZE,
    0,
  )
})

const fetchMoreEventComments = createAsyncThunk<
  CommentsWithPaginationResult,
  FetchMoreEventCommentsPayload,
  { state: RootState }
>('eventComments/fetchMoreEventComments', async (payload, thunkAPI) => {
  const state = thunkAPI.getState().eventComments

  return loadCommentsByRootPage(
    payload.target_id,
    payload.target_type,
    payload.ordering || state.ordering || 'new',
    ROOT_COMMENTS_PAGE_SIZE,
    state.rootOffset,
  )
})

const refetchLoadedEventComments = createAsyncThunk<
  CommentsWithPaginationResult,
  FetchEventCommentsPayload,
  { state: RootState }
>('eventComments/refetchLoadedEventComments', async (payload, thunkAPI) => {
  const state = thunkAPI.getState().eventComments
  const loadedRootLimit = Math.max(state.rootOffset, ROOT_COMMENTS_PAGE_SIZE)

  return loadCommentsByRootPage(
    payload.target_id,
    payload.target_type,
    payload.ordering || state.ordering || 'new',
    loadedRootLimit,
    0,
  )
})

const createEventComment = createAsyncThunk<void, CreateEventCommentPayload>(
  'eventComments/createEventComment',
  async (payload, thunkAPI) => {
    await appAPI.eventsComments.postEventsComment({
      targetId: payload.targetId,
      targetType: payload.targetType,
      message: payload.message,
      parent: payload.parent,
    })

    await thunkAPI.dispatch(
      refetchLoadedEventComments({
        target_id: payload.target_id_for_refetch,
        target_type: payload.targetType as 'event' | 'news',
        ordering: payload.ordering || 'new',
      }) as any,
    )
  },
)

const updateEventComment = createAsyncThunk<void, UpdateEventCommentPayload>(
  'eventComments/updateEventComment',
  async (payload, thunkAPI) => {
    await appAPI.eventsComments.putEventsComment({
      id: payload.id,
      message: payload.message,
    })

    await thunkAPI.dispatch(
      refetchLoadedEventComments({
        target_id: payload.target_id,
        target_type: payload.target_type,
        ordering: payload.ordering || 'new',
      }) as any,
    )
  },
)

const removeEventComment = createAsyncThunk<void, DeleteEventCommentPayload>(
  'eventComments/removeEventComment',
  async (payload, thunkAPI) => {
    await appAPI.eventsComments.deleteEventsComment(payload.id)

    await thunkAPI.dispatch(
      refetchLoadedEventComments({
        target_id: payload.target_id,
        target_type: payload.target_type,
        ordering: payload.ordering || 'new',
      }) as any,
    )
  },
)

const eventCommentsSlice = createSlice({
  name: 'eventComments',
  initialState,
  reducers: {
    clearEventComments: () => initialState,
    setCommentsOrdering: (state, action: { payload: CommentsOrdering }) => {
      state.ordering = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchEventComments.pending, (state) => {
        state.loading = true
        state.error = undefined
        state.comments = []
        state.rootOffset = 0
        state.hasMore = false
        state.totalTopLevelComments = 0
      })
      .addCase(fetchEventComments.fulfilled, (state, action) => {
        state.comments = action.payload.comments
        state.loading = false
        state.totalTopLevelComments = action.payload.totalTopLevelComments
        state.rootOffset = action.payload.nextOffset
        state.rootLimit = action.payload.rootLimit
        state.hasMore = action.payload.hasMore
      })
      .addCase(fetchEventComments.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message
      })

      .addCase(fetchMoreEventComments.pending, (state) => {
        state.loadingMore = true
        state.error = undefined
      })
      .addCase(fetchMoreEventComments.fulfilled, (state, action) => {
        const currentIds = new Set(state.comments.map((comment) => comment.id))
        const nextComments = action.payload.comments.filter((comment) => !currentIds.has(comment.id))

        state.comments = [...state.comments, ...nextComments]
        state.loadingMore = false
        state.totalTopLevelComments = action.payload.totalTopLevelComments
        state.rootOffset = action.payload.nextOffset
        state.rootLimit = action.payload.rootLimit
        state.hasMore = action.payload.hasMore
      })
      .addCase(fetchMoreEventComments.rejected, (state, action) => {
        state.loadingMore = false
        state.error = action.error.message
      })

      .addCase(refetchLoadedEventComments.pending, (state) => {
        state.error = undefined
      })
      .addCase(refetchLoadedEventComments.fulfilled, (state, action) => {
        state.comments = action.payload.comments
        state.totalTopLevelComments = action.payload.totalTopLevelComments
        state.rootOffset = action.payload.nextOffset
        state.rootLimit = action.payload.rootLimit
        state.hasMore = action.payload.hasMore
      })
      .addCase(refetchLoadedEventComments.rejected, (state, action) => {
        state.error = action.error.message
      })

      .addCase(createEventComment.pending, (state) => {
        state.submitting = true
        state.error = undefined
      })
      .addCase(createEventComment.fulfilled, (state) => {
        state.submitting = false
      })
      .addCase(createEventComment.rejected, (state, action) => {
        state.submitting = false
        state.error = action.error.message
      })

      .addCase(updateEventComment.pending, (state) => {
        state.submitting = true
        state.error = undefined
      })
      .addCase(updateEventComment.fulfilled, (state) => {
        state.submitting = false
      })
      .addCase(updateEventComment.rejected, (state, action) => {
        state.submitting = false
        state.error = action.error.message
      })

      .addCase(removeEventComment.pending, (state) => {
        state.submitting = true
        state.error = undefined
      })
      .addCase(removeEventComment.fulfilled, (state) => {
        state.submitting = false
      })
      .addCase(removeEventComment.rejected, (state, action) => {
        state.submitting = false
        state.error = action.error.message
      })
  },
})

export default eventCommentsSlice.reducer

export const eventCommentsActions = {
  ...eventCommentsSlice.actions,
  fetchEventComments,
  fetchMoreEventComments,
  refetchLoadedEventComments,
  createEventComment,
  updateEventComment,
  removeEventComment,
}
